import type { SxProps, Theme } from "@mui/material";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { PickersActionBarAction } from "@mui/x-date-pickers";
import {
  DatePicker,
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarIcon } from "components/icons";
import { useGlobalStore } from "controller/store/globalStore";
import type { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { dayjs } from "utilz";
import { convertStringToSentenceCase } from "utilz/helpers";

interface DateInputProps {
  className?: string;
  sx?: SxProps<Theme>;
  value: Dayjs | string | null;
  label?: string;
  onChangeHandler: (value: Dayjs | null | string) => void;
  size?: "small" | "large" | "medium";
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
  error?: boolean;
  timezone?: string;
  errorMessage?: string;
  hideHelperBox?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: library requirement
  slots?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: library requirement
  slotProps?: Record<string, any>;
  fullWidth?: boolean;
  backgourndColor?: string;
  variant?: "outlined" | "text" | "standard"; // New prop to control the variant
  showCloseButton?: boolean; // Add this prop
}

const EmptyIcon = () => <Box sx={{ width: "24px", height: "24px" }} />;

/** The desktop calendar popup's root class. */
const CALENDAR_POPPER = ".MuiPickersPopper-root";

/**
 * The picker's open state, with our own click-away. MUI closes the calendar
 * on a document `click`, but the shared Drawer stops click propagation, so
 * inside a drawer that click never arrives and the calendar stays open. A
 * capture-phase listener runs before anything can stop it.
 */
const usePickerOpenState = () => {
  const [open, setOpen] = useState(false);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnClickAway = (event: MouseEvent) => {
      // Desktop popper only — the mobile picker is a modal dialog that
      // closes itself.
      if (!document.querySelector(CALENDAR_POPPER)) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      const isInCalendar =
        target instanceof Element && target.closest(CALENDAR_POPPER) !== null;
      if (isInCalendar) return;
      // The calendar button toggles: close, and don't let its own click
      // reopen the calendar.
      if (openButtonRef.current?.contains(target)) event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("click", closeOnClickAway, true);
    return () => document.removeEventListener("click", closeOnClickAway, true);
  }, [open]);

  return {
    openState: {
      open,
      onOpen: () => setOpen(true),
      onClose: () => setOpen(false),
    },
    openButtonRef,
  };
};
export const DateInput = ({
  className,
  sx,
  value,
  onChangeHandler,
  label,
  size = "small",
  disabled = false,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  error = false,
  timezone,
  errorMessage,
  hideHelperBox = true,
  slots,
  slotProps,
  fullWidth,
  backgourndColor,
  variant = "outlined", // Default to outlined
  showCloseButton = false, // Add this with default value
}: DateInputProps) => {
  const theme = useTheme();
  const dateFormat =
    useGlobalStore((state) => state.dateFormat) || "MM/DD/YYYY";
  const { openState, openButtonRef } = usePickerOpenState();

  if (variant === "text") {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          {...openState}
          className={className}
          disableFuture={disableFuture}
          disablePast={disablePast}
          disabled={disabled}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          timezone={timezone}
          label={label ? convertStringToSentenceCase(label) : ""}
          value={value}
          format={dateFormat}
          onChange={(newValue) => onChangeHandler(newValue)}
          slots={{
            ...slots,
            textField: (params) => {
              const inputProps = {
                ...params.inputProps,
                readOnly: true, // Make the input read-only
              };

              return (
                <Stack spacing={1}>
                  <TextField
                    {...params}
                    inputProps={inputProps}
                    size={size}
                    error={error}
                    fullWidth={fullWidth}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "none",
                        backgroundColor: "transparent",
                        "& > fieldset": {
                          border: "none",
                        },
                        "&:hover > fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused > fieldset": {
                          border: "none",
                        },
                      },
                      ...sx,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                      ...params.InputProps,
                    }}
                  />
                  {errorMessage && (
                    <Typography variant="body-small" color="error">
                      {errorMessage}
                    </Typography>
                  )}
                </Stack>
              );
            },
          }}
          slotProps={{
            openPickerButton: { ref: openButtonRef },
            desktopPaper: {
              sx: {
                "& .MuiPickersDay-root": {
                  borderRadius: "6px",
                  ":hover": {
                    bgcolor: theme.palette.neutral["005"],
                  },
                  ...theme.typography["body-small"],
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.purple.main,
                    color: "#FFF",
                  },
                  "&.MuiPickersDay-today": {
                    border: `1px solid ${theme.palette.purple.main}`,
                  },
                },
                borderRadius: "16px",
                // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
                // boxShadow: "0px 5px 5px -3px rgba(0, 0, 0, 0.2)",
                // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
                // boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14)",
                boxShadow: "0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
              },
            },
            ...slotProps,
          }}
        />
      </LocalizationProvider>
    );
  }

  if (variant === "standard") {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          cancelButtonLabel: "Close",
        }}
      >
        <DatePicker
          {...openState}
          disableFuture={disableFuture}
          disablePast={disablePast}
          disabled={disabled}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          timezone={timezone}
          label={label ? convertStringToSentenceCase(label) : ""}
          value={value}
          format={dateFormat}
          onChange={(newValue) => onChangeHandler(newValue)}
          slots={{
            openPickerIcon: disabled ? EmptyIcon : CalendarIcon,
            ...slots,
          }}
          slotProps={{
            openPickerButton: {
              ref: openButtonRef,
              color: error ? "error" : "inherit",
            },
            // actionBar: {
            //   sx: { ...theme.typography.caption },
            // },
            actionBar: {
              actions: showCloseButton
                ? (["cancel"] as PickersActionBarAction[])
                : [],
              // cancelButtonText: "Close",
              sx: {
                ...theme.typography.caption,
                "&.MuiDialogActions-root": {
                  color: `${theme.palette.purple.main} !important`,
                  "& .MuiButtonBase-root": {
                    ...theme.typography.caption,
                    color: `${theme.palette.purple.main} !important`,
                    textTransform: "none", // Add this line
                  },
                },
              },
            },
            textField: {
              variant: "standard",
              helperText: (
                <Box
                  sx={{
                    mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
                  }}
                >
                  <Typography variant="body-small" color="error">
                    {errorMessage}
                  </Typography>
                </Box>
              ),
              size: size,
              error: error,
              fullWidth: fullWidth,
              sx: {
                ...theme.typography["body-medium"],
                "& .MuiInput-root": {
                  pr: "4px",
                  backgroundColor: backgourndColor
                    ? backgourndColor
                    : "transparent",
                  "&:before": {
                    borderBottomColor: error
                      ? theme.palette.error.main
                      : theme.palette.neutral["020"],
                    borderBottomWidth: "1px",
                  },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottomColor: error
                      ? theme.palette.error.main
                      : theme.palette.lightBg.medium,
                    borderBottomWidth: "1px",
                  },
                  "&:after": {
                    borderBottomColor: theme.palette.purple.main,
                    borderBottomWidth: "2px",
                  },
                  "& input::placeholder": { fontSize: "14px" },
                },
                "& .MuiInput-root.Mui-focused": {
                  color: theme.palette.lightBg.high,
                },
                "& label": {
                  "&.Mui-focused": { color: theme.palette.purple.main },
                },
                ...sx,
              },
            },
            desktopPaper: {
              sx: {
                "& .MuiPickersDay-root": {
                  borderRadius: "6px",
                  ":hover": {
                    bgcolor: theme.palette.neutral["005"],
                  },
                  ...theme.typography["body-small"],
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.purple.main,
                    color: "#FFF",
                  },
                  "&.MuiPickersDay-today": {
                    border: `1px solid ${theme.palette.purple.main}`,
                  },
                },
                borderRadius: "16px",
                // boxShadow: "0px 5px 5px -3px rgba(0, 0, 0, 0.2)",
                // boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14)",
                boxShadow: "0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
              },
            },
            ...slotProps,
          }}
        />
      </LocalizationProvider>
    );
  }
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      localeText={{
        cancelButtonLabel: "Close",
      }}
    >
      <DatePicker
        {...openState}
        disableFuture={disableFuture}
        disablePast={disablePast}
        disabled={disabled}
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        timezone={timezone}
        label={label ? convertStringToSentenceCase(label) : ""}
        value={value}
        format={dateFormat}
        onChange={(newValue) => onChangeHandler(newValue)}
        slots={{
          openPickerIcon: disabled ? EmptyIcon : CalendarIcon,
          ...slots,
        }}
        slotProps={{
          openPickerButton: {
            ref: openButtonRef,
            color: error ? "error" : "inherit",
          },
          // actionBar: {
          //   sx: { ...theme.typography.caption },
          // },
          actionBar: {
            actions: showCloseButton
              ? (["cancel"] as PickersActionBarAction[])
              : [],
            sx: {
              ...theme.typography.caption,
              "&.MuiDialogActions-root": {
                color: `${theme.palette.purple.main} !important`,
                "& .MuiButtonBase-root": {
                  ...theme.typography.caption,
                  color: `${theme.palette.purple.main} !important`,
                  textTransform: "none", // Add this line
                },
              },
            },
          },
          textField: {
            helperText: (
              <Box
                sx={{
                  mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
                }}
              >
                <Typography variant="body-small" color="error">
                  {errorMessage}
                </Typography>
              </Box>
            ),
            size: size,
            error: error,
            fullWidth: fullWidth,
            sx: {
              ...theme.typography["body-medium"],
              "& .MuiOutlinedInput-root": {
                height: { sm: "32px" },
                backgroundColor: backgourndColor
                  ? backgourndColor
                  : error
                    ? theme.palette.error.tint
                    : "white",
                "& > fieldset": {
                  borderColor: theme.palette.neutral["020"],
                  borderRadius: "4px",
                },
                "& input::placeholder": { fontSize: "14px" },
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                color: theme.palette.lightBg.high,
                "& > fieldset": { borderColor: theme.palette.purple.main },
              },
              "& label": {
                "&.Mui-focused": { color: theme.palette.purple.main },
                // MUI's resting offset is for a 40px field; centre it in the
                // 32px one, same as Input and SelectField.
                "&:not(.MuiInputLabel-shrink)": {
                  transform: "translate(14px, 6px) scale(1)",
                },
              },
              ...sx,
            },
          },
          desktopPaper: {
            sx: {
              "& .MuiPickersDay-root": {
                borderRadius: "6px",
                ":hover": {
                  bgcolor: theme.palette.neutral["005"],
                },
                ...theme.typography["body-small"],
                "&.Mui-selected": {
                  backgroundColor: theme.palette.purple.main,
                  color: "#FFF",
                },
                "&.MuiPickersDay-today": {
                  border: `1px solid ${theme.palette.purple.main}`,
                },
              },
              borderRadius: "16px",
              // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
              // boxShadow: "0px 5px 5px -3px rgba(0, 0, 0, 0.2)",
              // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
              // boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14)",
              boxShadow: "0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
            },
          },
          ...slotProps,
        }}
      />
    </LocalizationProvider>
  );
};

// import React from "react";
// import {
//   LocalizationProvider,
//   DesktopDatePicker,
//   DatePicker,
// } from "@mui/x-date-pickers";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import {
//   Box,
//   InputAdornment,
//   SxProps,
//   TextField,
//   Theme,
//   Typography,
//   useTheme,
// } from "@mui/material";
// import { Dayjs } from "dayjs";
// import { CalendarIcon, InfoFilledIcon } from "components/icons";
// import { useGlobalStore } from "controller/store/globalStore";
// import { dayjs } from "utilz";

// interface DateInputProps {
//   sx?: SxProps<Theme>;
//   value: Dayjs | string | null;
//   label?: string;
//   onChangeHandler: (value: Dayjs | null | string) => void;
//   size?: "small" | "large" | "medium";
//   disabled?: boolean;
//   disableFuture?: boolean;
//   disablePast?: boolean;
//   minDate?: any;
//   maxDate?: any;
//   error?: boolean;
//   timezone?: string;
//   errorMessage?: string;
//   hideHelperBox?: boolean;
//   slots?: Record<string, any>;
//   slotProps?: Record<string, any>;
//   fullWidth?: boolean;
//   backgourndColor?: string;
// }

// const EmptyIcon = () => <Box sx={{ width: "24px", height: "24px" }} />;
// export const DateInput = ({
//   sx,
//   value,
//   onChangeHandler,
//   label,
//   size = "small",
//   disabled = false,
//   disableFuture = false,
//   disablePast = false,
//   minDate,
//   maxDate,
//   error = false,
//   timezone,
//   errorMessage,
//   hideHelperBox = true,
//   slots,
//   slotProps,
//   fullWidth,
//   backgourndColor,
// }: DateInputProps) => {
//   const theme = useTheme();
//   const dateFormat =
//     useGlobalStore((state) => state.dateFormat) || "MM/DD/YYYY";

//   return (
//     <LocalizationProvider dateAdapter={AdapterDayjs}>
//       <DatePicker
//         disableFuture={disableFuture}
//         disablePast={disablePast}
//         disabled={disabled}
//         minDate={minDate ? dayjs(minDate) : undefined}
//         maxDate={maxDate ? dayjs(maxDate) : undefined}
//         timezone={timezone}
//         label={label}
//         value={value}
//         format={dateFormat}
//         onChange={(newValue) => onChangeHandler(newValue)}
//         slots={{
//           openPickerIcon: disabled ? EmptyIcon : CalendarIcon,

//           ...slots,
//         }}
//         slotProps={{
//           openPickerButton: {
//             color: error ? "error" : "inherit",
//           },
//           actionBar: {
//             sx: { ...theme.typography.caption },
//           },
//           textField: {
//             helperText: (
//               <Box
//                 sx={{
//                   mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
//                 }}
//               >
//                 <Typography variant="body-small" color="error">
//                   {errorMessage}
//                 </Typography>
//               </Box>
//             ),
//             size: size,
//             error: error,
//             fullWidth: fullWidth,
//             // InputProps: {
//             //   endAdornment: (
//             //     <InputAdornment position="end">
//             //       <CalendarIcon />
//             //     </InputAdornment>
//             //   ),
//             // },
//             sx: {
//               ...theme.typography["body-medium"],
//               "& .MuiOutlinedInput-root": {
//                 height: { sm: "32px" },
//                 backgroundColor: backgourndColor
//                   ? backgourndColor
//                   : error
//                   ? theme.palette.error.tint
//                   : "white",
//                 "& > fieldset": {
//                   borderColor: theme.palette.neutral["020"],
//                   borderRadius: "4px",
//                 },
//                 "& input::placeholder": { fontSize: "14px" },
//               },
//               "& .MuiOutlinedInput-root.Mui-focused": {
//                 color: theme.palette.lightBg.high,
//                 "& > fieldset": { borderColor: theme.palette.purple.main },
//               },
//               "& label": {
//                 "&.Mui-focused": { color: theme.palette.purple.main },
//               },
//               ...sx,
//             },
//           },
//           desktopPaper: {
//             sx: {
//               "& .MuiPickersDay-root": {
//                 borderRadius: "6px",
//                 ":hover": {
//                   bgcolor: theme.palette.neutral["005"],
//                 },
//                 ...theme.typography["body-small"],
//                 "&.Mui-selected": {
//                   backgroundColor: theme.palette.purple.main,
//                   color: "#FFF",
//                 },
//                 "&.MuiPickersDay-today": {
//                   border: `1px solid ${theme.palette.purple.main}`,
//                 },
//               },
//               borderRadius: "16px",
//               boxShadow: "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
//             },
//           },
//           ...slotProps,
//         }}
//       />
//     </LocalizationProvider>
//   );
// };
